// Inbox mode-policy adapter — Wave 5c Agent 5c.4 (EPIC-07).
//
// Wraps `@barel/core`'s `resolveAiMode` (the canonical state-machine for
// per-account AI mode + off-target stance + hot-lead override + channel-share
// pause) into a compact, UI-friendly policy that the inbox thread surfaces
// consume. The core resolver is pure-data; this module adds:
//
//   - `detectChannelShareInAiMessage()` — the regex/keyword detector for
//     calendar / phone / email tokens used by `AIPauseOnSensitive` (US-089).
//     Detection runs against the AI's most recent outbound text. Server-side
//     equivalent ships in Wave 6 (relances scheduler) — the inbox shadows the
//     same logic so the user sees the pause banner before the server round-trip.
//
//   - `derivePolicy()` — collects per-account + per-conversation + per-voice
//     state, runs `resolveAiMode`, and emits a typed render-decision struct
//     that names which slot banner the thread should mount.
//
// US-090 (no-`manual` mode): the per-account `aiMode` enum is locked to
// `'auto' | 'review'` for the UI surface. The `'manual'` token still exists
// in `@barel/core` for off-target stance ("⊘ Ne rien faire") + reserved
// future use; it is NOT exposed as a per-account choice. See `docs/ai-modes.md`
// for the full rationale.

import {
  resolveAiMode,
  type AiMode,
  type AiModeResolveOutput,
  type EffectiveAiMode,
  type NotifyHotAction,
  type OffTargetMode,
} from '@barel/core';

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

/**
 * The two AI modes the UI exposes per account (US-090).
 * Note that `@barel/core` still exports a wider `AiMode = 'auto' | 'review'
 * | 'manual'` for off-target compatibility — `manual` is intentionally not
 * surfaced as a per-account choice anywhere in the inbox.
 */
export type AccountAiMode = Extract<AiMode, 'auto' | 'review'>;

/** Snapshot of the conversation's AI-policy-relevant state. */
export interface ConversationModeState {
  isVeryHot: boolean;
  offTarget: boolean;
  /** True when AI auto-pushed this conv to Leads via auto-detect (US-087). */
  autoDetected: boolean;
  /**
   * Set when the AI's most recent outbound message contains a calendar /
   * phone / email token (US-089). Detection is owned by
   * `detectChannelShareInAiMessage()` below and runs over the latest AI
   * outbound text whenever the thread renders.
   */
  channelShareLocked: boolean;
}

/** Snapshot of the LinkedIn account's policy-relevant settings. */
export interface AccountModeState {
  aiMode: AccountAiMode;
  offTargetMode: OffTargetMode;
  autoLeadDetect: boolean;
}

/** Snapshot of the voice config that drives hot-lead notify behaviour. */
export interface VoiceModeState {
  notifyHotAction: NotifyHotAction;
}

export interface ModePolicyInput {
  account: AccountModeState;
  voice: VoiceModeState;
  conversation: ConversationModeState;
}

export interface ModePolicyDecision {
  /** Effective mode after every override has been applied. */
  effectiveMode: EffectiveAiMode;
  /** The raw account-level mode (before overrides) — for diagnostics + footer. */
  accountMode: AccountAiMode;
  /** Reason emitted by the core resolver. */
  reason: AiModeResolveOutput['reason'];
  /** True if any override fired (channel-share / off-target / hot-override). */
  overridden: boolean;
  /** Convenience flags the thread surfaces consume directly. */
  flags: {
    /** Mount `<HotOverride>` (US-088). */
    showHotOverride: boolean;
    /** Mount `<OffTargetHandler>` (US-085, US-086). */
    showOffTargetHandler: boolean;
    /** Mount `<AutoLeadDetectIndicator>` (US-087). */
    showAutoLeadIndicator: boolean;
    /** Mount `<AIPauseOnSensitive>` (US-089). */
    showAIPause: boolean;
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Channel-share detection (US-089)
//
// Detects calendar / phone / email tokens in AI-authored outbound text. The
// list is intentionally narrow (per the business "Bon à savoir" footnote in
// `prototypes_and_docs/ls-business.jsx:1245-1251`): sending a PDF / deck does
// NOT trigger pause — only off-LinkedIn channel hand-offs do.
//
// The detector is regex-driven for browser execution; the same logic ships
// server-side in Wave 6 as a structured AI extraction pass on outbound msgs.
// Both sides MUST agree on a positive match — if the regex fires, the server
// will too. We intentionally err on false-positive: a literal "Calendly link
// below" with no actual URL still matches because the user prefers a pause.
// ────────────────────────────────────────────────────────────────────────────

export interface ChannelShareDetection {
  matched: boolean;
  tokens: readonly ('calendar' | 'phone' | 'email')[];
}

const CALENDAR_PROVIDERS = [
  'calendly',
  'cal.com',
  'savvycal',
  'youcanbookme',
  'doodle',
  'lemcal',
  'tidycal',
  'meet.google.com',
  'zcal.co',
];

// Keywords that imply scheduling intent even without a URL ("réservez un
// créneau"). Kept narrow to avoid matching marketing copy ("we'll be at the
// conference next week").
const CALENDAR_KEYWORDS = ['/booking/', '/meetings/', '/scheduling/', '/schedule/'];

// Matches phone-shaped runs: optional leading `+`, then 8+ digits possibly
// interspersed with whitespace / parens / dashes / dots. Built as
// `\+?(?:\d[\s().-]*){8,}` so each digit can be followed by zero-or-more
// separator chars — covers `+33 6 12 34 56 78`, `(415) 555-0117`, `+1.415.555.0117`.
const PHONE_REGEX = /\+?(?:\d[\s().-]*){8,}/u;

const EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/iu;

/**
 * Returns the set of channel-share tokens present in an AI-authored outbound
 * message body. Empty arrays for empty / null inputs.
 *
 * Used by:
 *   - `<AIPauseOnSensitive>` to render the pause banner (US-089).
 *   - `derivePolicy()` to set `channelShareLocked` when the consumer hasn't
 *     pre-computed it (e.g. tests, optimistic UI before server confirms).
 */
export function detectChannelShareInAiMessage(
  text: string | null | undefined,
): ChannelShareDetection {
  const tokens = new Set<'calendar' | 'phone' | 'email'>();
  if (!text) return { matched: false, tokens: [] };
  const lower = text.toLowerCase();

  for (const provider of CALENDAR_PROVIDERS) {
    if (lower.includes(provider)) {
      tokens.add('calendar');
      break;
    }
  }
  if (!tokens.has('calendar')) {
    for (const kw of CALENDAR_KEYWORDS) {
      if (lower.includes(kw)) {
        tokens.add('calendar');
        break;
      }
    }
  }

  if (EMAIL_REGEX.test(text)) tokens.add('email');
  // Run phone after email so we don't double-count digits inside an email.
  // Strip any matched email out before regex-ing for phone numbers.
  const withoutEmail = text.replace(new RegExp(EMAIL_REGEX, 'giu'), ' ');
  if (PHONE_REGEX.test(withoutEmail)) tokens.add('phone');

  return {
    matched: tokens.size > 0,
    tokens: [...tokens],
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Policy aggregator
// ────────────────────────────────────────────────────────────────────────────

/**
 * Applies `resolveAiMode` from `@barel/core` and returns a UI-ready
 * decision struct. Fully deterministic: same input → same output. The four
 * `flags.show*` booleans are mutually compatible — multiple banners may
 * mount at once (e.g. hot + auto-detected). The thread is responsible for
 * stacking order; this module does not impose one.
 */
export function derivePolicy(input: ModePolicyInput): ModePolicyDecision {
  const { account, voice, conversation } = input;

  const resolved = resolveAiMode({
    accountMode: account.aiMode,
    offTarget: conversation.offTarget,
    offTargetMode: account.offTargetMode,
    isVeryHot: conversation.isVeryHot,
    hotAction: voice.notifyHotAction,
    channelShareLocked: conversation.channelShareLocked,
  });

  const flags = {
    showHotOverride: resolved.reason === 'hot_override_validate',
    showOffTargetHandler: conversation.offTarget,
    showAutoLeadIndicator: account.autoLeadDetect && conversation.autoDetected,
    showAIPause: resolved.reason === 'channel_share_locked',
  };

  return {
    effectiveMode: resolved.mode,
    accountMode: account.aiMode,
    reason: resolved.reason,
    overridden: resolved.overridden,
    flags,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Off-target banner copy keys (US-086)
//
// Consumers map this to i18n keys via `inbox.offTarget.banner.<copyKey>`.
// Centralised here so component + tests share a single source of truth.
// ────────────────────────────────────────────────────────────────────────────

export type OffTargetCopyKey = 'block' | 'suggest' | 'nothing';

export function offTargetCopyKey(mode: OffTargetMode): OffTargetCopyKey {
  if (mode === 'block') return 'block';
  if (mode === 'suggest') return 'suggest';
  return 'nothing';
}
